import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useUserContext } from 'UserContext';
import { RecruitNotification } from 'recruit/models';
import {
    getRecruitNotifications,
    markAllRecruitNotificationsRead,
    markRecruitNotificationRead,
} from 'recruit/api/notifications';
import { useNotificationContext } from '../NotificationContext/NotificationContext';
import { consoleLogInDev } from 'shared';

interface RecruitNotificationContextType {
    notifications: RecruitNotification[];
    unreadCount: number;
    loaded: boolean;
    hasMore: boolean;
    loadNotifications: () => void;
    loadMore: () => void;
    markRead: (notification: RecruitNotification) => Promise<void>;
    markAllRead: () => Promise<void>;
}

export const RecruitNotificationContext = React.createContext<RecruitNotificationContextType>({
    notifications: [],
    unreadCount: 0,
    loaded: false,
    hasMore: false,
    loadNotifications: () => undefined,
    loadMore: () => undefined,
    markRead: async () => undefined,
    markAllRead: async () => undefined,
});

export function RecruitNotificationProvider({ children }: { children: React.ReactNode }) {
    const { user, hasRecruitAccess } = useUserContext();
    const { recruitUnreadCount: unreadCount, setRecruitUnreadCount: setUnreadCount } = useNotificationContext();
    const [notifications, setNotifications] = useState<RecruitNotification[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [wanted, setWanted] = useState(false);
    const enabled = Boolean(user) && hasRecruitAccess();

    const fetchPage = useCallback((pageNumber: number) => {
        return getRecruitNotifications(pageNumber)
            .then((result) => {
                setNotifications((prev) => (pageNumber === 1 ? result.results : [...prev, ...result.results]));
                setHasMore(result.next !== null);
                setPage(pageNumber);
            })
            .catch(consoleLogInDev);
    }, []);

    useEffect(() => {
        if (!wanted || loaded) return;
        if (!enabled) {
            if (user) setLoaded(true);
            return;
        }
        fetchPage(1).finally(() => setLoaded(true));
    }, [wanted, loaded, enabled, user, fetchPage]);

    const loadNotifications = () => setWanted(true);

    const loadMore = () => {
        if (hasMore) fetchPage(page + 1);
    };

    const markRead = async (notification: RecruitNotification) => {
        if (notification.read) return;
        try {
            await markRecruitNotificationRead(notification);
            setNotifications((prev) => prev.map((row) => (row.id === notification.id ? { ...row, read: true } : row)));
            setUnreadCount((count) => Math.max(0, count - 1));
        } catch (err) {
            consoleLogInDev(err);
        }
    };

    const markAllRead = async () => {
        if (unreadCount === 0) return;
        try {
            await markAllRecruitNotificationsRead();
            setNotifications((prev) => prev.map((row) => ({ ...row, read: true })));
            setUnreadCount(0);
        } catch (err) {
            consoleLogInDev(err);
        }
    };

    return (
        <RecruitNotificationContext.Provider
            value={{ notifications, unreadCount, loaded, hasMore, loadNotifications, loadMore, markRead, markAllRead }}
        >
            {children}
        </RecruitNotificationContext.Provider>
    );
}

export const useRecruitNotificationContext = () => useContext(RecruitNotificationContext);
